// Toggles visibility of the curated "search initial content" panel rendered
// by snippets/search-initial-content.liquid. The panel sits inside the search
// modal (desktop + mobile) and is shown only while the search input is empty.
// When the user types, the panel hides so the predictive-search results take
// over the modal body.
//
// Also wires the Latest News prev/next arrows (desktop) — they scroll the
// article grid horizontally by one card width and disable when at either end.

interface PanelHost extends HTMLElement {
	dataset: DOMStringMap & { _searchInitialInit?: string };
}

const INPUT_SELECTOR: string = '#Search-In-Modal';
const PANEL_SELECTOR: string = '[data-search-initial]';

const showPanel: (panel: PanelHost) => void = (panel: PanelHost): void => {
	panel.hidden = false;
	panel.setAttribute('data-open', 'true');
};

const hidePanel: (panel: PanelHost) => void = (panel: PanelHost): void => {
	panel.hidden = true;
	panel.removeAttribute('data-open');
};

const syncFromInput: (input: HTMLInputElement, panel: PanelHost) => void = (
	input: HTMLInputElement,
	panel: PanelHost
): void => {
	const isEmpty: boolean = input.value.trim().length === 0;
	if (isEmpty) {
		showPanel(panel);
	} else {
		hidePanel(panel);
	}
};

const updateArrowState: (
	grid: HTMLElement,
	prevBtn: HTMLButtonElement,
	nextBtn: HTMLButtonElement
) => void = (
	grid: HTMLElement,
	prevBtn: HTMLButtonElement,
	nextBtn: HTMLButtonElement
): void => {
	const max: number = grid.scrollWidth - grid.clientWidth;
	const epsilon: number = 2;
	const atStart: boolean = grid.scrollLeft <= epsilon;
	const atEnd: boolean = grid.scrollLeft >= max - epsilon;
	prevBtn.disabled = atStart;
	prevBtn.classList.toggle('swiper-button-disabled', atStart);
	nextBtn.disabled = atEnd;
	nextBtn.classList.toggle('swiper-button-disabled', atEnd);
};

const bindArrowCarousel: (
	panel: PanelHost,
	gridSelector: string,
	prevSelector: string,
	nextSelector: string,
	itemSelector: string
) => void = (
	panel: PanelHost,
	gridSelector: string,
	prevSelector: string,
	nextSelector: string,
	itemSelector: string
): void => {
	const grid: HTMLElement | null = panel.querySelector<HTMLElement>(gridSelector);
	const prevBtn: HTMLButtonElement | null = panel.querySelector<HTMLButtonElement>(prevSelector);
	const nextBtn: HTMLButtonElement | null = panel.querySelector<HTMLButtonElement>(nextSelector);
	if (!grid || !prevBtn || !nextBtn) return;

	const step: () => number = (): number => {
		const firstCell: HTMLElement | null = grid.querySelector<HTMLElement>(itemSelector);
		return firstCell ? firstCell.getBoundingClientRect().width : grid.clientWidth / 4;
	};

	prevBtn.addEventListener('click', (): void => {
		grid.scrollBy({ left: -step(), behavior: 'smooth' });
	});
	nextBtn.addEventListener('click', (): void => {
		grid.scrollBy({ left: step(), behavior: 'smooth' });
	});
	grid.addEventListener('scroll', (): void => updateArrowState(grid, prevBtn, nextBtn));

	// The panel is `hidden` (display: none) on initial render, so scrollWidth and
	// clientWidth both read as 0 at init — that wrongly marks the next button as
	// disabled and the user has no way to trigger the scroll event. A ResizeObserver
	// on the grid catches the moment the panel opens (0 → real width), the moment
	// the viewport resizes, and any unexpected reflow.
	if (typeof ResizeObserver !== 'undefined') {
		const ro: ResizeObserver = new ResizeObserver((): void => {
			updateArrowState(grid, prevBtn, nextBtn);
		});
		ro.observe(grid);
	} else {
		// Fallback for older browsers — recheck after the panel transitions in.
		window.setTimeout((): void => updateArrowState(grid, prevBtn, nextBtn), 0);
	}
};

const bindNewsDots: (panel: PanelHost) => void = (panel: PanelHost): void => {
	const grid: HTMLElement | null = panel.querySelector<HTMLElement>('[data-search-initial-news-grid]');
	const dotsList: HTMLElement | null = panel.querySelector<HTMLElement>('[data-search-initial-news-dots]');
	if (!grid || !dotsList) return;

	const slides: HTMLElement[] = Array.from(
		grid.querySelectorAll<HTMLElement>('.search-initial-content__news-item')
	);
	const dots: HTMLButtonElement[] = Array.from(
		dotsList.querySelectorAll<HTMLButtonElement>('[data-search-initial-news-dot]')
	);
	if (slides.length === 0 || dots.length === 0) return;

	const setActive: (index: number) => void = (index: number): void => {
		dots.forEach((dot: HTMLButtonElement, i: number): void => {
			if (i === index) {
				dot.setAttribute('aria-current', 'true');
			} else {
				dot.removeAttribute('aria-current');
			}
		});
	};

	dots.forEach((dot: HTMLButtonElement, i: number): void => {
		dot.addEventListener('click', (): void => {
			const slide: HTMLElement | undefined = slides[i];
			if (!slide) return;
			grid.scrollTo({ left: slide.offsetLeft - grid.offsetLeft, behavior: 'smooth' });
		});
	});

	// Track which slide is most visible. The carousel uses scroll-snap so usually
	// exactly one slide is centered, but during a fling we pick the most-visible.
	const observer: IntersectionObserver = new IntersectionObserver(
		(entries: IntersectionObserverEntry[]): void => {
			let bestIndex: number = -1;
			let bestRatio: number = 0;
			entries.forEach((entry: IntersectionObserverEntry): void => {
				if (entry.intersectionRatio > bestRatio) {
					bestRatio = entry.intersectionRatio;
					bestIndex = slides.indexOf(entry.target as HTMLElement);
				}
			});
			if (bestIndex !== -1) setActive(bestIndex);
		},
		{ root: grid, threshold: [0.5, 0.75, 1] }
	);
	slides.forEach((slide: HTMLElement): void => observer.observe(slide));

	setActive(0);
};

const bindPanel: (panel: PanelHost) => void = (panel: PanelHost): void => {
	if (panel.dataset._searchInitialInit === 'true') return;
	panel.dataset._searchInitialInit = 'true';

	// The input lives in the same modal subtree as the panel; the predictive-search
	// custom element wraps both, so we scope to the closest container.
	const scope: HTMLElement =
		(panel.closest('predictive-search') as HTMLElement | null) ||
		(panel.closest('.search-modal') as HTMLElement | null) ||
		(panel.closest('.search-modal-mobile') as HTMLElement | null) ||
		(panel.parentElement as HTMLElement) ||
		document.body;

	const input: HTMLInputElement | null = scope.querySelector<HTMLInputElement>(INPUT_SELECTOR);
	if (input) {
		input.addEventListener('focus', (): void => syncFromInput(input, panel));
		input.addEventListener('click', (): void => syncFromInput(input, panel));
		input.addEventListener('input', (): void => syncFromInput(input, panel));

		// The theme's search "reset" button clears the input via form reset, which
		// fires before the value is actually cleared. Defer one tick.
		const form: HTMLFormElement | null = input.form;
		if (form) {
			form.addEventListener('reset', (): void => {
				window.setTimeout((): void => syncFromInput(input, panel), 0);
			});
		}
	}

	// Watch the modal wrapper for `.active` being added. The theme's
	// searchOpen() (assets/search-modal.js) adds `.active` and then calls
	// .focus() on the input — but on mobile the focus event sometimes fails
	// to reach our listener (input is inside a drawer that hasn't finished
	// transitioning, or focus fires before our handler runs). Observing the
	// class directly is a more reliable hook: when the modal opens and the
	// input is empty, show the initial-content panel immediately.
	const modal: HTMLElement | null =
		(panel.closest('.search-modal-mobile') as HTMLElement | null) ||
		(panel.closest('.search-modal') as HTMLElement | null);
	if (modal && input) {
		const onModalActive: () => void = (): void => {
			if (modal.classList.contains('active')) {
				syncFromInput(input, panel);
			}
		};
		const mo: MutationObserver = new MutationObserver(onModalActive);
		mo.observe(modal, { attributes: true, attributeFilter: ['class'] });
	}

	bindArrowCarousel(
		panel,
		'[data-search-initial-news-grid]',
		'[data-search-initial-news-prev]',
		'[data-search-initial-news-next]',
		'.search-initial-content__news-item'
	);
	bindArrowCarousel(
		panel,
		'[data-search-initial-products-grid]',
		'[data-search-initial-products-prev]',
		'[data-search-initial-products-next]',
		'.search-initial-content__product-item'
	);
	bindNewsDots(panel);
};

// Predictive search results are injected via innerHTML into elements with
// `[data-predictive-search]`. Each render replaces the previous DOM, so we
// re-bind the carousel arrows + dots every time new results appear.
const bindCarouselsInRoot: (root: HTMLElement) => void = (root: HTMLElement): void => {
	const panel: PanelHost = root as PanelHost;
	bindArrowCarousel(
		panel,
		'[data-search-initial-news-grid]',
		'[data-search-initial-news-prev]',
		'[data-search-initial-news-next]',
		'.search-initial-content__news-item'
	);
	bindArrowCarousel(
		panel,
		'[data-search-initial-products-grid]',
		'[data-search-initial-products-prev]',
		'[data-search-initial-products-next]',
		'.search-initial-content__product-item'
	);
	bindNewsDots(panel);
};

const observePredictiveSearch: () => void = (): void => {
	const wrappers: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(
		'[data-predictive-search]'
	);
	wrappers.forEach((wrapper: HTMLElement): void => {
		if (wrapper.dataset._predictiveBound === 'true') return;
		wrapper.dataset._predictiveBound = 'true';

		const mo: MutationObserver = new MutationObserver((): void => {
			const inserted: HTMLElement | null = wrapper.querySelector<HTMLElement>(
				'[data-predictive-search-content]'
			);
			if (inserted) bindCarouselsInRoot(inserted);
		});
		mo.observe(wrapper, { childList: true, subtree: true });
	});
};

const initSearchInitialContent: () => void = (): void => {
	document.querySelectorAll<PanelHost>(PANEL_SELECTOR).forEach(bindPanel);
	observePredictiveSearch();
};

let sectionLoadBound: boolean = false;

const searchInitialContent: () => void = (): void => {
	initSearchInitialContent();
	initLiveSearchRefresh();

	if (sectionLoadBound) return;
	sectionLoadBound = true;
	document.addEventListener('shopify:section:load', (event: Event): void => {
		const target: Element | null = event.target as Element | null;
		if (!target) return;
		target.querySelectorAll<PanelHost>(PANEL_SELECTOR).forEach(bindPanel);
	});
};

// ────────────────────────────────────────────────────────────────────────────
// Live section refresh for /search — typing in ANY search input (header pill
// or in-page mobile form) re-fetches the main-search section via Shopify's
// `?section_id=` rendering API and swaps the section's innerHTML in place.
// No page reload, no popup. Heading + counts + tabs + grid all update inline.
//
// Event delegation on `document` so the listener survives the swap (the
// input element itself is part of the replaced HTML each time). After the
// swap, inline `<script>` tags inside the new markup are cloned into fresh
// script elements to force re-execution (count-fetch + infinite-scroll IIFEs
// have to re-bind).
// ────────────────────────────────────────────────────────────────────────────

const SEARCH_INPUT_SELECTOR: string = 'input[name="q"][type="search"]';
const SECTION_INNER_SELECTOR: string = '.template-search';
const SECTION_PREFIX: string = 'shopify-section-';
const REFRESH_DEBOUNCE_MS: number = 300;

const getSearchSectionEl: () => HTMLElement | null = (): HTMLElement | null => {
	const inner: HTMLElement | null = document.querySelector<HTMLElement>(SECTION_INNER_SELECTOR);
	if (!inner) return null;
	return inner.closest<HTMLElement>(`[id^="${SECTION_PREFIX}"]`);
};

const reexecuteInlineScripts: (root: HTMLElement) => void = (root: HTMLElement): void => {
	root.querySelectorAll<HTMLScriptElement>('script').forEach((oldScript: HTMLScriptElement): void => {
		const newScript: HTMLScriptElement = document.createElement('script');
		for (let i: number = 0; i < oldScript.attributes.length; i++) {
			const attr: Attr = oldScript.attributes[i];
			newScript.setAttribute(attr.name, attr.value);
		}
		newScript.textContent = oldScript.textContent;
		oldScript.parentNode?.replaceChild(newScript, oldScript);
	});
};

let refreshAbort: AbortController | null = null;
let refreshDebounce: number | null = null;

const refreshSearchSection: (input: HTMLInputElement) => void = (input: HTMLInputElement): void => {
	const sectionEl: HTMLElement | null = getSearchSectionEl();
	if (!sectionEl) return;
	const sectionId: string = sectionEl.id.replace(new RegExp(`^${SECTION_PREFIX}`), '');

	const q: string = input.value;
	if (refreshAbort) refreshAbort.abort();
	refreshAbort = new AbortController();

	const params: URLSearchParams = new URLSearchParams(window.location.search);
	const currentType: string = params.get('type') || 'product';

	const url: string =
		'/search?q=' + encodeURIComponent(q) +
		'&options%5Bprefix%5D=last' +
		'&type=' + currentType +
		'&section_id=' + sectionId;

	fetch(url, { signal: refreshAbort.signal, credentials: 'same-origin' })
		.then((r: Response): Promise<string> => {
			if (!r.ok) throw new Error(String(r.status));
			return r.text();
		})
		.then((html: string): void => {
			const doc: Document = new DOMParser().parseFromString(html, 'text/html');
			const newSection: HTMLElement | null = doc.getElementById(SECTION_PREFIX + sectionId);
			if (!newSection) return;

			const activeEl: Element | null = document.activeElement;
			const hadFocus: boolean = !!(activeEl && activeEl.id === 'Search-In-Template');
			let caretPos: number | null = null;
			if (hadFocus && activeEl instanceof HTMLInputElement) caretPos = activeEl.selectionStart;

			sectionEl.innerHTML = newSection.innerHTML;
			reexecuteInlineScripts(sectionEl);

			if (hadFocus) {
				const newInput: HTMLInputElement | null = document.getElementById(
					'Search-In-Template'
				) as HTMLInputElement | null;
				if (newInput) {
					newInput.focus();
					if (caretPos !== null) newInput.setSelectionRange(caretPos, caretPos);
				}
			}

			const newUrl: string =
				window.location.pathname +
				'?q=' + encodeURIComponent(q) +
				'&type=' + currentType;
			window.history.replaceState({}, '', newUrl);
		})
		.catch((err: Error): void => {
			if (err && err.name === 'AbortError') return;
		});
};

let liveSearchBound: boolean = false;

const initLiveSearchRefresh: () => void = (): void => {
	if (liveSearchBound) return;
	liveSearchBound = true;
	document.addEventListener('input', (e: Event): void => {
		const target: EventTarget | null = e.target;
		if (!(target instanceof HTMLInputElement)) return;
		if (!target.matches(SEARCH_INPUT_SELECTOR)) return;
		// Gate to the search results page — `.template-search` is only in the
		// DOM on /search, so this no-ops everywhere else even though it's on
		// document.
		if (!getSearchSectionEl()) return;
		if (refreshDebounce !== null) window.clearTimeout(refreshDebounce);
		refreshDebounce = window.setTimeout(
			(): void => refreshSearchSection(target),
			REFRESH_DEBOUNCE_MS
		);
	});
};

export default searchInitialContent;
